// Step 3 — Phone verification (Figma 14-18).
//
// The phone is verified by Registration's actor phone API, the one the membership site uses
// (React-Membership, pages/phone), unchanged: the same 4-digit code, reCAPTCHA v3 on every send
// ("phone_send") and on the check ("phone_verify"), and a 30 s wait between sends. Registration then records
// the step on the submission (decided 2026-10-06, option A). A phone already confirmed skips the
// step. Country code is preselected from the IP geo guess; "change phone number" goes back.

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
// @ts-ignore — no bundled types for this version
import PhoneInput from "react-phone-input-2";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import SubmissionCodeInput, { shouldClearCode } from "../common/SubmissionCodeInput";
import ResendCountdown from "../common/ResendCountdown";
import { getSubmissionFlowApi, isMockApi } from "../../../shared/api/submission";
import { isPlausiblePhone, PHONE_CODE_LENGTH, toE164 } from "../../../shared/api/submission/phoneVerification";
import { ISubmissionDraftDto } from "../../../shared/api/submission/types";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { getTrackingData } from "../../../helpers";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { trackSubmissionEvent } from "../analytics";
import SuccessCheckIcon from "../../../images/success-check.svg";
import { membershipPrimary } from "../common/membershipButton";
import { onPlan } from "../../../models/submissionStep4Model";
import { autoContinueLabel, useAutoContinue } from "../common/autoContinue";
import { prefetch, reviewKey } from "../common/prefetch";
import { getBreakdownInfo } from "../common/breakdownInfo";
import SignedInAs from "../common/SignedInAs";
import { recaptchaTokenWhenReady } from "../common/recaptchaToken";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const PhoneVerificationInner: React.FC<Props> = ({
  history,
  location,
  breakdownId,
  roleId,
  loadMembership,
  geoGuess,
  phone,
  maskedPhone,
  phoneCodeSent,
  phoneStatus,
  phoneResendAvailableAt,
  loading,
  errorCode,
  errorMessage,
  sendPhoneCode,
  verifyPhoneCode,
  loadPhoneStatus,
  recordPhone,
  ensureDraft,
  refreshGate,
  clearError,
}) => {
  const [localPhone, setLocalPhone] = useState(phone || "");
  const [countryCode, setCountryCode] = useState("");
  const [code, setCode] = useState("");
  // The latest errorCode, for code that runs after an await (the prop in a handler's
  // closure is the one from before the request).
  const errorCodeRef = useRef<string | null>(errorCode);
  errorCodeRef.current = errorCode;
  const [editingNumber, setEditingNumber] = useState(!phoneCodeSent);
  const [localError, setLocalError] = useState<string | null>(null);
  // Until we know whether the account's phone is already confirmed.
  const [checking, setChecking] = useState(true);
  const autoContinue = useAutoContinue(() => goNext());
  const recaptcha = useGoogleReCaptcha();
  // The hook's latest value: a press made while the script loads waits for it (recaptchaToken.ts).
  const recaptchaRef = useRef(recaptcha);
  recaptchaRef.current = recaptcha;
  // A send or a code check under way, from the press (its token first, which can wait for the
  // script) to the answer: the number, the code and Resend wait, a second press does nothing.
  const [pending, setPending] = useState(false);
  // Left during that wait (Back): nothing is sent for a page no one is on.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  // Recording the phone, started as soon as the code is right (preloadStep4).
  const recording = useRef<Promise<ISubmissionDraftDto | null> | null>(null);

  useEffect(() => {
    // Every flow URL must carry `bd`. Without it there is nothing to submit
    // to, so hand the visitor to the traditional actor signup.
    if (!hasFlowContext({ search: location.search, breakdownId })) {
      history.replace(fallbackTo(location.search));
      return;
    }
    if (!isSubmissionAuthed()) {
      history.replace(`/submission/flow/verify-email${location.search}`);
      return;
    }
    trackSubmissionEvent("sf_phone_step_viewed", {});
    // A stale error from Step 2 must not render under this step's copy.
    clearError();
    refreshGate({ history, search: location.search });
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The draft (after a refresh it isn't in memory), then the account's phone: one already
  // confirmed is recorded and skipped; one on file but unconfirmed pre-fills the input.
  const start = async () => {
    // Already on Pro or a trial: there is no phone to verify (decided 2026-10-27).
    if (onPlan(await loadMembership())) {
      trackSubmissionEvent("sf_phone_skipped_on_plan", {});
      history.replace(`/submission/flow/step/4${location.search}`);
      return;
    }
    await ensureDraft({ breakdownId, roleId });
    const status = await loadPhoneStatus();
    if (status && status.isVerified) {
      trackSubmissionEvent("sf_phone_already_verified", {});
      if (await finish()) return;
    } else if (status && status.phone && !localPhone) {
      setLocalPhone(status.phone.replace(/[^0-9]/g, ""));
    }
    setChecking(false);
  };

  // Records the step and moves on. Without the materials, back to step 2. Returns whether it
  // left the page.
  const finish = async (): Promise<boolean> => {
    const early = recording.current;
    recording.current = null;
    // The early record when it went through; otherwise asked again, as before.
    const draft: ISubmissionDraftDto | null = (early && (await early)) || (await recordPhone());
    if (draft) {
      history.replace(`/submission/flow/step/4${location.search}`);
      return true;
    }
    return false;
  };

  useEffect(() => {
    // Step 2 isn't done: the materials, or the film center a new signup we can't place must pick.
    if (errorCode === "materialsRequired" || errorCode === "homeLocationRequired") {
      history.replace(`/submission/flow/step/2${location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  // A fresh token per request, as the membership site does. Same placeholder rules as step 1.
  const getRecaptchaToken = async (action: string): Promise<string> => {
    if (isMockApi()) return "mock-recaptcha-token";
    if (process.env.NODE_ENV === "development" && process.env.REACT_APP_RECAPTCHA_DEV_BYPASS === "true")
      return "dev-recaptcha-bypass";
    return recaptchaTokenWhenReady(() => recaptchaRef.current && recaptchaRef.current.executeRecaptcha, action);
  };

  const defaultCountry = (
    geoGuess && geoGuess.countryCode ? geoGuess.countryCode : "ca"
  ).toLowerCase();

  // Every send carries a fresh token, resends too. The API skips the check for a resend while the
  // last code is still valid, but checks it once that code has expired; the membership site sends
  // none on a resend, so an expired code's resend is refused there as "not human".
  const onSend = async (isResend = false) => {
    if (loading || pending) return;
    // The number checked is the number sent (the field waits meanwhile).
    const phone = toE164(localPhone);
    const phoneCountry = countryCode || defaultCountry.toUpperCase();
    if (!isPlausiblePhone(phone)) {
      setLocalError(i18next.t("That phone number doesn't look right."));
      return;
    }
    setLocalError(null);
    setPending(true);
    const recaptchaToken = await getRecaptchaToken("phone_send");
    if (!mounted.current) return;
    let resp: any;
    try {
      resp = await sendPhoneCode({ phone, countryCode: phoneCountry, recaptchaToken });
    } finally {
      if (mounted.current) setPending(false);
    }
    if (resp) {
      trackSubmissionEvent(isResend ? "sf_phone_code_resent" : "sf_phone_code_sent", {});
      setEditingNumber(false);
      setCode("");
    }
  };

  // During "Phone verified"'s count: record the phone and ask step 4's first calls (the
  // breakdown check, the review), so step 4 opens ready instead of on its skeleton. The same
  // requests, only earlier; step 4 takes the answers (common/prefetch). Decided 2026-10-28.
  const preloadStep4 = () => {
    if (breakdownId) {
      // Kept, so step 4's gate check takes it (common/breakdownInfo).
      getBreakdownInfo(breakdownId, roleId).catch(() => undefined);
    }
    recording.current = recordPhone();
    recording.current.then((draft) => {
      if (draft && draft.submissionId) {
        prefetch(reviewKey(draft.submissionId), () => getSubmissionFlowApi().getSubmissionReview(draft.submissionId));
      }
    });
  };

  const goNext = async () => {
    autoContinue.stop();
    await finish();
  };

  const onComplete = async (value: string) => {
    if (pending) return;
    let trackingData: object = {};
    try {
      trackingData = getTrackingData() || {};
    } catch (e) {
      trackingData = {};
    }
    setPending(true);
    const recaptchaToken = await getRecaptchaToken("phone_verify");
    if (!mounted.current) return;
    let resp: any;
    try {
      resp = await verifyPhoneCode({ code: value, recaptchaToken, trackingData });
    } finally {
      if (mounted.current) setPending(false);
    }
    if (resp) {
      trackSubmissionEvent("sf_phone_verified", {});
      preloadStep4();
      // On by itself after the count on Continue (Continue still works).
      autoContinue.start();
    } else if (shouldClearCode(errorCodeRef.current)) {
      // A wrong or dead code empties the boxes; a transient failure keeps it for Enter.
      setCode("");
    }
  };

  if (checking) {
    return (
      <SubmissionLayout step={3}>
        <CenterWrap aria-busy="true">
          <SubTitle>{i18next.t("One moment…")}</SubTitle>
        </CenterWrap>
      </SubmissionLayout>
    );
  }

  if (phoneStatus === "verified") {
    return (
      <SubmissionLayout step={3}>
        <CenterWrap>
          <SuccessIcon src={SuccessCheckIcon} alt="" aria-hidden="true" />
          <Title role="status">{i18next.t("Phone verified!")}</Title>
          <SubTitle>
            {i18next.t("Almost there! You are getting closer to submitting.")}
          </SubTitle>
          {errorCode && errorCode !== "materialsRequired" && errorCode !== "homeLocationRequired" && (
            <ErrorText role="alert">
              {errorCode === "network"
                ? errorMessage
                : i18next.t("We couldn't confirm your phone. Please try again.")}
            </ErrorText>
          )}
          <PrimaryButton onClick={goNext} disabled={loading}>{autoContinueLabel(autoContinue.secondsLeft)}</PrimaryButton>
        </CenterWrap>
      </SubmissionLayout>
    );
  }

  const digits = (localPhone || "").replace(/[^0-9]/g, "");
  const coolingDown = phoneResendAvailableAt > Date.now();
  const sendErrorText = localError
    ? localError
    : errorCode === "invalidPhone"
    ? i18next.t("That phone number doesn't look right.")
    : errorCode === "phoneAlreadyInUse"
    ? i18next.t("That number is already verified on another account.")
    : errorCode === "phoneCountryNotSupported"
    ? i18next.t("We can't verify that number. Check it, or use another one.")
    : errorCode === "recaptchaFailed"
    ? i18next.t("We couldn't verify that you're human. Please try again.")
    : errorCode === "sendLimit"
    ? i18next.t("Too many codes sent. Try again later.")
    : errorCode === "resendCooldown"
    ? i18next.t("Please wait before sending another code.")
    : errorCode === "network"
    ? errorMessage
    : errorCode
    ? i18next.t("Something went wrong. Please try again.")
    : null;
  // Cooling down must disable the button, not produce a silent no-op tap.
  const canSend = digits.length >= 8 && !loading && !pending && !coolingDown;

  return (
    <SubmissionLayout step={3}>
      <CenterWrap>
        <Title>{i18next.t("Verify your phone number")}</Title>
        <SubTitle>
          {editingNumber
            ? i18next.t("Input your phone number and we'll send you a code")
            : `${i18next.t("Enter the code we sent to")} ${maskedPhone}`}
        </SubTitle>
      </CenterWrap>

      {editingNumber ? (
        <>
          <PhoneWrap
            onKeyDown={(e: React.KeyboardEvent) => {
              // Enter in the number field sends the code, as the button would, only while it
              // is enabled. Not from the country picker, where Enter opens or picks a country.
              if (e.key !== "Enter" || (e.nativeEvent as any).isComposing || !canSend) return;
              if ((e.target as HTMLInputElement).type !== "tel") return;
              e.preventDefault();
              onSend(false);
            }}
          >
            <PhoneInput
              country={defaultCountry}
              value={localPhone}
              onChange={(value: string, data: any) => {
                setLocalPhone(value);
                setLocalError(null);
                if (data && data.countryCode) setCountryCode(data.countryCode.toUpperCase());
              }}
              disabled={pending}
              inputProps={{ autoFocus: true, "aria-label": i18next.t("Phone number") }}
              containerStyle={{ width: "100%" }}
              inputStyle={{ width: "100%", height: "46px" }}
            />
          </PhoneWrap>

          {sendErrorText && <ErrorText role="alert">{sendErrorText}</ErrorText>}

          <PrimaryButton onClick={() => onSend(false)} disabled={!canSend}>
            {loading || pending
              ? i18next.t("One moment…")
              : coolingDown
              ? i18next.t("Please wait before sending another code.")
              : i18next.t("Send code")}
          </PrimaryButton>
          <TextButton
            onClick={() => {
              clearError();
              history.goBack();
            }}
          >
            {i18next.t("Back")}
          </TextButton>
        </>
      ) : (
        <>
          <SubmissionCodeInput
            length={PHONE_CODE_LENGTH}
            value={code}
            onChange={setCode}
            onComplete={onComplete}
            isInvalid={phoneStatus === "error" || phoneStatus === "locked"}
            isDisabled={phoneStatus === "verifying" || phoneStatus === "locked" || pending}
            isChecking={phoneStatus === "verifying" || pending}
            ariaLabel={i18next.t("Enter the {{length}} digit code we sent by text", { length: PHONE_CODE_LENGTH })}
          />

          {errorCode && (
            <ErrorText role="alert">
              {errorCode === "invalidCode"
                ? i18next.t("That code isn't right. Check it and try again.")
                : errorCode === "codeExpired"
                ? i18next.t("That code has expired. Tap Resend to get a new one.")
                : errorCode === "tooManyAttempts"
                ? i18next.t("Too many attempts. Please try again later.")
                : errorCode === "recaptchaFailed"
                ? i18next.t("We couldn't verify that you're human. Please try again.")
                : errorCode === "phoneCountryNotSupported"
                ? i18next.t("We can't verify that number. Check it, or use another one.")
                : errorCode === "sendLimit"
                ? i18next.t("Too many codes sent. Try again later.")
                : errorCode === "network"
                ? errorMessage
                : i18next.t("We couldn't check your code — try again")}
            </ErrorText>
          )}

          <ResendCountdown
            availableAt={phoneResendAvailableAt}
            onResend={() => onSend(true)}
            disabled={phoneStatus === "verifying" || pending}
          />

          <TextButton
            onClick={() => {
              // Don't carry the code-entry error into the number editor, whose
              // copy has no mapping for it.
              clearError();
              setEditingNumber(true);
              setCode("");
            }}
          >
            {i18next.t("Change phone number")}
          </TextButton>
        </>
      )}

      <SignedInAs history={history} search={location.search} step={3} />

      {isMockApi() && (
        <MockHint>mock sms code: 6767 · 5555555xx → already in use</MockHint>
      )}
    </SubmissionLayout>
  );
};

const CenterWrap = styled.div`
  text-align: center;
  margin: 40px 0 24px;
`;

// The membership app's own check (React-Membership pages/phone: success-check.svg, 60px), so
// "Phone verified" looks the same in both apps.
const SuccessIcon = styled.img`
  height: 60px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 10px;
`;

const SubTitle = styled.p`
  font-size: 14px;
  color: #53627c;
  margin: 0 0 8px;
  line-height: 21px;
`;

const PhoneWrap = styled.div`
  margin-bottom: 14px;

  .react-tel-input .form-control {
    font-size: 15px;
    border-radius: 5px;
    border-color: #c9d3e0;
  }
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #c7053f;
  text-align: center;
  margin-bottom: 12px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 0;
  cursor: pointer;
  min-height: 44px;
`;

const MockHint = styled.div`
  margin-top: 20px;
  font-size: 11px;
  color: #8a94a6;
  font-family: monospace;
  text-align: center;
`;

interface StateProps {
  breakdownId: string | null;
  roleId: string | null;
  geoGuess: iRootState["submissionFlowModel"]["geoGuess"];
  phone: string;
  maskedPhone: string;
  phoneCodeSent: boolean;
  phoneStatus: string;
  phoneResendAvailableAt: number;
  phoneAttemptsRemaining: number;
  loading: boolean;
  errorCode: string | null;
  errorMessage: string | null;
}

interface DispatchProps {
  loadMembership: () => Promise<any>;
  sendPhoneCode: (payload: { phone: string; countryCode: string; recaptchaToken?: string }) => Promise<any>;
  verifyPhoneCode: (payload: { code: string; recaptchaToken?: string; trackingData?: object }) => Promise<any>;
  loadPhoneStatus: () => Promise<{ isVerified: boolean; phone?: string } | null>;
  recordPhone: () => Promise<ISubmissionDraftDto | null>;
  ensureDraft: (payload: { breakdownId: string | null; roleId: string | null }) => Promise<any>;
  refreshGate: (payload: { history: any; search?: string }) => Promise<any>;
  clearError: () => void;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  breakdownId: state.submissionFlowModel.breakdownId,
  roleId: state.submissionFlowModel.roleId,
  geoGuess: state.submissionFlowModel.geoGuess,
  phone: state.submissionSignupModel.phone,
  maskedPhone: state.submissionSignupModel.maskedPhone,
  phoneCodeSent: state.submissionSignupModel.phoneCodeSent,
  phoneStatus: state.submissionSignupModel.phoneStatus,
  phoneResendAvailableAt: state.submissionSignupModel.phoneResendAvailableAt,
  phoneAttemptsRemaining: state.submissionSignupModel.phoneAttemptsRemaining,
  loading: state.submissionSignupModel.loading,
  errorCode: state.submissionSignupModel.errorCode,
  errorMessage: state.submissionSignupModel.errorMessage,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  loadMembership: dispatch.submissionStep4Model.loadMembership,
  sendPhoneCode: dispatch.submissionSignupModel.sendPhoneCode,
  verifyPhoneCode: dispatch.submissionSignupModel.verifyPhoneCode,
  loadPhoneStatus: dispatch.submissionSignupModel.loadPhoneStatus,
  recordPhone: dispatch.submissionSignupModel.recordPhone,
  ensureDraft: dispatch.submissionSignupModel.ensureDraft,
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  clearError: dispatch.submissionSignupModel.clearError,
});

// reCAPTCHA v3 needs a provider above the hook. With no site key (mock env) the provider is
// skipped and no Google script loads, as on step 1.
const PhoneVerificationPage: React.FC<Props> = (props) => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (!siteKey) return <PhoneVerificationInner {...props} />;
  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
      <PhoneVerificationInner {...props} />
    </GoogleReCaptchaProvider>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(PhoneVerificationPage);
